import * as React from "react";
import { loadDemoPlayer, DemoPlayer, DemoVoice, PlayOpts } from "./demoAudio";

const BASE = process.env.PUBLIC_URL || "";

/* Interactive "try now" demos for the vision techniques. Everything is
   pre-rendered by scripts/render_experiential_demo_images.py with the
   product's own symbolic-prompt engine — the visitor explores cached
   renders; the marketing site makes no API calls. */

function FadeImage({ srcs, active, alt }: { srcs: string[]; active: number; alt: string }) {
  return (
    <div className="vdemo-stage">
      {srcs.map((s, i) => (
        <img
          key={s}
          src={s}
          alt={i === active ? alt : ""}
          aria-hidden={i !== active}
          className={i === active ? "is-active" : ""}
          loading="lazy"
        />
      ))}
    </div>
  );
}

/* EXP-02: before ↔ after crossfade */
export function RescriptDemo() {
  const [after, setAfter] = React.useState(false);
  return (
    <div className="lib-ex">
      <span className="lib-ex__label">“a cold hallway, nobody coming” — rescript it yourself</span>
      <FadeImage
        srcs={[`${BASE}/demo/imagery-rescript/before.webp`, `${BASE}/demo/imagery-rescript/after.webp`]}
        active={after ? 1 : 0}
        alt={after ? "the rescripted scene — warmth returns" : "the remembered scene"}
      />
      <div className="vdemo-chips">
        <button type="button" className="vdemo-chip" onClick={() => setAfter((v) => !v)}>
          {after ? "back to the memory" : "▸ warmth returns, the door opens"}
        </button>
      </div>
    </div>
  );
}

/* EXP-12: walk the pre-rendered refinement tree until it fits */
type FeltNode = { src: string; note: string; chips: { label: string; to: string }[] };

const FELT_TREE: Record<string, FeltNode> = {
  base: {
    src: `${BASE}/demo/felt-sense/attempt_1.webp`,
    note: "“tight and grey, pressing on the chest”",
    chips: [
      { label: "heavier, like wet wool", to: "wool" },
      { label: "sharper, like thin ice", to: "ice" },
    ],
  },
  wool: {
    src: `${BASE}/demo/felt-sense/attempt_2.webp`,
    note: "closer… something is still missing",
    chips: [{ label: "warmer at the edges, like something loosening", to: "wool_warm" }],
  },
  ice: {
    src: `${BASE}/demo/felt-sense/attempt_ice.webp`,
    note: "closer… something is still missing",
    chips: [{ label: "warmer at the edges, like something loosening", to: "ice_warm" }],
  },
  wool_warm: {
    src: `${BASE}/demo/felt-sense/fits.webp`,
    note: "“that's it. that's the one.”",
    chips: [],
  },
  ice_warm: {
    src: `${BASE}/demo/felt-sense/fits_ice.webp`,
    note: "“that's it. that's the one.”",
    chips: [],
  },
};

const FELT_SRCS = Object.values(FELT_TREE).map((n) => n.src);

export function FeltSenseDemo() {
  const [node, setNode] = React.useState("base");
  const cur = FELT_TREE[node];
  return (
    <div className="lib-ex">
      <span className="lib-ex__label">refine the image until it fits — try it</span>
      <FadeImage srcs={FELT_SRCS} active={FELT_SRCS.indexOf(cur.src)} alt="symbolic render of the felt sense" />
      <p className="vdemo-note">{cur.note}</p>
      <div className="vdemo-chips">
        {cur.chips.map((c) => (
          <button key={c.to} type="button" className="vdemo-chip" onClick={() => setNode(c.to)}>
            ▸ {c.label}
          </button>
        ))}
        {node !== "base" && (
          <button type="button" className="vdemo-chip vdemo-chip--ghost" onClick={() => setNode("base")}>
            start over
          </button>
        )}
      </div>
    </div>
  );
}

/* EXP-14: pick the card that resonates */
const DECK = [1, 2, 3, 4, 5, 6].map((n) => `${BASE}/demo/metaphor-deck/card_${n}.webp`);

export function DeckDemo() {
  const [picked, setPicked] = React.useState<number | null>(null);
  return (
    <div className="lib-ex">
      <span className="lib-ex__label">six cards from “carrying something heavy” — pick one</span>
      {picked !== null && (
        <>
          <FadeImage srcs={DECK} active={picked} alt={`metaphor card ${picked + 1}`} />
          <p className="vdemo-note">this one resonates — tell me about it.</p>
        </>
      )}
      <div className={`lx-deck ${picked !== null ? "lx-deck--picking" : ""}`}>
        {DECK.map((src, i) => (
          <button key={src} type="button" className="vdemo-card" onClick={() => setPicked(i)}>
            <img src={src} alt={`metaphor card ${i + 1}`} loading="lazy" className={picked === i ? "is-selected" : ""} />
          </button>
        ))}
      </div>
    </div>
  );
}

/* EXP-15: step the scene away */
const DIST = [
  { src: `${BASE}/demo/distant-screen/near.webp`, label: "near — vivid, filling the frame" },
  { src: `${BASE}/demo/distant-screen/mid.webp`, label: "distant — smaller, muted" },
  { src: `${BASE}/demo/distant-screen/far.webp`, label: "far away — tiny on the horizon" },
];

export function DistantScreenDemo() {
  const [step, setStep] = React.useState(0);
  return (
    <div className="lib-ex">
      <span className="lib-ex__label">“the argument in the kitchen” — step away from it</span>
      <FadeImage srcs={DIST.map((d) => d.src)} active={step} alt="the scene at this distance" />
      <p className="vdemo-note">{DIST[step].label}</p>
      <div className="vdemo-chips">
        <button
          type="button"
          className="vdemo-chip"
          disabled={step >= DIST.length - 1}
          onClick={() => setStep((s) => Math.min(s + 1, DIST.length - 1))}
        >
          ▸ step back
        </button>
        <button
          type="button"
          className="vdemo-chip vdemo-chip--ghost"
          disabled={step === 0}
          onClick={() => setStep((s) => Math.max(s - 1, 0))}
        >
          step closer
        </button>
      </div>
    </div>
  );
}

/* Generic scene + voice demo: one rendered image, a quote, and play chips.
   chips default to a single "▶ hear it"; pass variants for defusion-style play. */
interface SceneVoiceDemoProps {
  label: string;
  image: string;
  alt: string;
  quote?: string;
  clip: DemoVoice;
  chips?: { label: string; opts: PlayOpts }[];
}

export function SceneVoiceDemo({ label, image, alt, quote, clip, chips }: SceneVoiceDemoProps) {
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const playerRef = React.useRef<DemoPlayer | null>(null);

  React.useEffect(() => () => playerRef.current?.close(), []);

  const hear = async (opts: PlayOpts = {}) => {
    setErr("");
    setBusy(true);
    try {
      if (!playerRef.current) playerRef.current = await loadDemoPlayer([clip]);
      await playerRef.current.play(opts);
    } catch {
      setErr("Audio preview is being prepared.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lib-ex">
      <span className="lib-ex__label">{label}</span>
      <FadeImage srcs={[image]} active={0} alt={alt} />
      {quote && <p className="lx-quote">“{quote}”</p>}
      <div className="vdemo-chips">
        {(chips ?? [{ label: "▶ hear it", opts: {} }]).map((c) => (
          <button key={c.label} type="button" className="vdemo-chip" disabled={busy} onClick={() => hear(c.opts)}>
            {c.label}
          </button>
        ))}
      </div>
      {err && <span className="cdemo-err">{err}</span>}
    </div>
  );
}

/* EXP-29: nightmare rescripting — two renders, two narrations */
const NIGHT_CLIPS: DemoVoice[] = [
  { mode: "nightmare", label: "The nightmare", line: "The door at the end opens by itself, and I can't move.", level: 90, file: `${BASE}/demo/nightmare/nightmare.mp3` },
  { mode: "rescript", label: "Rewritten", line: "This time the key is in my hand.", level: 92, file: `${BASE}/demo/nightmare/rescript.mp3` },
];

export function NightmareDemo() {
  const [after, setAfter] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const playerRef = React.useRef<DemoPlayer | null>(null);

  React.useEffect(() => () => playerRef.current?.close(), []);

  const hear = async (idx: number) => {
    setErr("");
    setBusy(true);
    try {
      if (!playerRef.current) playerRef.current = await loadDemoPlayer(NIGHT_CLIPS);
      // solo the chosen narration; show its scene
      playerRef.current.setGain(0, idx === 0 ? 0.9 : 0);
      playerRef.current.setGain(1, idx === 1 ? 0.92 : 0);
      setAfter(idx === 1);
      await playerRef.current.play();
    } catch {
      setErr("Audio preview is being prepared.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lib-ex">
      <span className="lib-ex__label">the recurring corridor — rewritten, rendered, rehearsed</span>
      <FadeImage
        srcs={[`${BASE}/demo/nightmare/scene.webp`, `${BASE}/demo/nightmare/rescript.webp`]}
        active={after ? 1 : 0}
        alt={after ? "the rewritten dream, morning light" : "the recurring nightmare"}
      />
      <div className="vdemo-chips">
        <button type="button" className="vdemo-chip" disabled={busy} onClick={() => hear(0)}>
          ▶ the nightmare
        </button>
        <button type="button" className="vdemo-chip" disabled={busy} onClick={() => hear(1)}>
          ▶ the rescript
        </button>
      </div>
      {err && <span className="cdemo-err">{err}</span>}
    </div>
  );
}

/* EXP-30: pendulation — swing between resource and stressor, narration underneath */
const PEND_CLIP: DemoVoice = {
  mode: "pacing", label: "Pacing", line: "Now, just for three breaths, glance at the heavy thing…", level: 92, file: `${BASE}/demo/pendulation/pacing.mp3`,
};

export function PendulationDemo() {
  const [atResource, setAtResource] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const playerRef = React.useRef<DemoPlayer | null>(null);

  React.useEffect(() => () => playerRef.current?.close(), []);

  const hear = async () => {
    setErr("");
    setBusy(true);
    try {
      if (!playerRef.current) playerRef.current = await loadDemoPlayer([PEND_CLIP]);
      await playerRef.current.play();
    } catch {
      setErr("Audio preview is being prepared.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lib-ex">
      <span className="lib-ex__label">three breaths at a time — always back to the meadow</span>
      <FadeImage
        srcs={[`${BASE}/demo/pendulation/resource.webp`, `${BASE}/demo/pendulation/stressor.webp`]}
        active={atResource ? 0 : 1}
        alt={atResource ? "the meadow where nothing is required" : "the heavy thing, at the edge of the field"}
      />
      <div className="vdemo-chips">
        <button type="button" className="vdemo-chip" onClick={() => setAtResource((v) => !v)}>
          {atResource ? "▸ glance at the heavy thing" : "▸ back to the meadow"}
        </button>
        <button type="button" className="vdemo-chip vdemo-chip--ghost" disabled={busy} onClick={hear}>
          ▶ pacing narration
        </button>
      </div>
      {err && <span className="cdemo-err">{err}</span>}
    </div>
  );
}

/* EXP-26/27/28: ready-made scene+voice demos */
export const MiracleDemo = () => (
  <SceneVoiceDemo
    label="“suppose tonight, while you slept, it was solved…”"
    image={`${BASE}/demo/miracle/morning.webp`}
    alt="the morning after the miracle"
    quote="I'd wake before the alarm. Coffee would just be coffee."
    clip={{ mode: "morning", label: "The morning", line: "", level: 92, file: `${BASE}/demo/miracle/morning.mp3` }}
  />
);

export const MonsterDemo = () => (
  <SceneVoiceDemo
    label="the worry, out-arguable at last — shrink it yourself"
    image={`${BASE}/demo/worry-monster/monster.webp`}
    alt="the worry as a small friendly creature"
    quote="I am the Worry! I never sleep!"
    clip={{ mode: "monster", label: "The Worry", line: "", level: 92, file: `${BASE}/demo/worry-monster/monster.mp3` }}
    chips={[
      { label: "▶ big and loud", opts: {} },
      { label: "▶ squeaky", opts: { rate: 1.7 } },
      { label: "▶ sleepy", opts: { rate: 0.55 } },
      { label: "▶ far away", opts: { muffled: true } },
    ]}
  />
);

export const BraveDemo = () => (
  <SceneVoiceDemo
    label="first morning, one brave thing at a time"
    image={`${BASE}/demo/brave/threshold.webp`}
    alt="the school doorway on the first morning"
    quote="Find the desk, take one breath. One brave thing at a time."
    clip={{ mode: "plan", label: "The plan", line: "", level: 92, file: `${BASE}/demo/brave/plan.mp3` }}
  />
);

/* EXP-16: the compassionate other, seen and heard (real cached Soniox clip) */
const OTHER_CLIP = [{
  mode: "presence_words",
  label: "The compassionate other",
  line: "You are safe now. You never had to earn this.",
  level: 95,
  file: `${BASE}/demo/compassionate-other/presence_words.mp3`,
}];

export function CompassionateOtherDemo() {
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const playerRef = React.useRef<DemoPlayer | null>(null);

  React.useEffect(() => () => playerRef.current?.close(), []);

  const hear = async () => {
    setErr("");
    setBusy(true);
    try {
      if (!playerRef.current) playerRef.current = await loadDemoPlayer(OTHER_CLIP);
      await playerRef.current.play();
    } catch {
      setErr("Audio preview is being prepared.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lib-ex">
      <span className="lib-ex__label">seen, then heard — real render, real voice</span>
      <FadeImage srcs={[`${BASE}/demo/compassionate-other/presence.webp`]} active={0} alt="symbolic render of the compassionate other" />
      <p className="lx-quote">“You are safe now. You never had to earn this.”</p>
      <div className="vdemo-chips">
        <button type="button" className="vdemo-chip" onClick={hear} disabled={busy}>
          {busy ? "loading…" : "▶ hear them"}
        </button>
      </div>
      {err && <span className="cdemo-err">{err}</span>}
    </div>
  );
}
